import {
	type DrawFn,
	getAccent2Rgb,
	getAccentRgb,
	getPrimaryRgb,
	prefersReducedMotion,
	rgba,
} from "./types";

export type { DrawFn } from "./types";

const TAU = Math.PI * 2;
const REDUCED = prefersReducedMotion();
const DIM_RGB = "120,132,140";

const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;
const easeIO = (k: number): number => (k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2);
const rr = (
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	w: number,
	h: number,
	r: number,
): void => {
	ctx.beginPath();
	ctx.roundRect(x, y, w, h, r);
};

export interface CostCurveLabels {
	traditional: string;
	graphless: string;
	sleeping: string;
	active: string;
}

export interface CompatLabels {
	yourApp: string;
}

const DEFAULT_COMPAT_LABELS: CompatLabels = {
	yourApp: "your app",
};

const DEFAULT_TIME_TRAVEL_LABELS = ["week", "yesterday", "1h", "now"];

const DEFAULT_COST_CURVE_LABELS: CostCurveLabels = {
	traditional: "Traditional database — running 24/7",
	graphless: "Graphless — pays only when using",
	sleeping: "sleeping",
	active: "active",
};

export function costCurve(labels?: CostCurveLabels): DrawFn {
	const L = labels ?? DEFAULT_COST_CURVE_LABELS;
	const traffic = (x: number): number => {
		const g = (c: number, s: number): number => Math.exp(-((x - c) * (x - c)) / (2 * s * s));
		return Math.min(1, 0.05 + 0.95 * g(0.34, 0.12) + 0.78 * g(0.7, 0.1));
	};

	return (ctx, W, H, t) => {
		const padL = 14,
			padR = 14,
			padT = 26,
			padB = 30;
		const x0 = padL,
			x1 = W - padR,
			y0 = padT,
			y1 = H - padB;
		const cw = x1 - x0;
		const ch = y1 - y0;
		const topY = y0 + ch * 0.12;
		const baseY = y1;
		const X = (u: number): number => x0 + cw * u;
		const Y = (v: number): number => baseY - (baseY - topY) * v;
		ctx.clearRect(0, 0, W, H);

		ctx.strokeStyle = "rgba(255,255,255,0.06)";
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(x0, baseY);
		ctx.lineTo(x1, baseY);
		ctx.stroke();

		const N = 120;
		const sweep = REDUCED ? 1 : Math.min(1, (t % 6400) / 4200);
		const cut = X(sweep);
		const acc = getAccentRgb();

		ctx.beginPath();
		ctx.moveTo(x0, topY);
		ctx.lineTo(cut, topY);
		for (let i = Math.round(N * sweep); i >= 0; i--) {
			const u = i / N;
			ctx.lineTo(X(u), Y(traffic(u)));
		}
		ctx.closePath();
		ctx.fillStyle = "rgba(255,107,107,0.10)";
		ctx.fill();

		ctx.strokeStyle = "rgba(255,107,107,0.7)";
		ctx.lineWidth = 2;
		ctx.setLineDash([5, 5]);
		ctx.beginPath();
		ctx.moveTo(x0, topY);
		ctx.lineTo(x1, topY);
		ctx.stroke();
		ctx.setLineDash([]);

		ctx.beginPath();
		ctx.moveTo(x0, baseY);
		const maxI = Math.round(N * sweep);
		for (let i = 0; i <= maxI; i++) {
			const u = i / N;
			ctx.lineTo(X(u), Y(traffic(u)));
		}
		ctx.lineTo(cut, baseY);
		ctx.closePath();
		const grad = ctx.createLinearGradient(0, topY, 0, baseY);
		grad.addColorStop(0, rgba(acc, 0.34));
		grad.addColorStop(1, rgba(acc, 0.02));
		ctx.fillStyle = grad;
		ctx.fill();

		ctx.strokeStyle = rgba(acc, 0.95);
		ctx.lineWidth = 2.4;
		ctx.beginPath();
		for (let i = 0; i <= maxI; i++) {
			const u = i / N;
			const px = X(u);
			const py = Y(traffic(u));
			if (i) ctx.lineTo(px, py);
			else ctx.moveTo(px, py);
		}
		ctx.stroke();

		const tv = traffic(sweep);
		ctx.fillStyle = "rgba(255,107,107,0.95)";
		ctx.beginPath();
		ctx.arc(cut, topY, 3.5, 0, TAU);
		ctx.fill();

		const gx = cut;
		const gy = Y(tv);
		const gl = ctx.createRadialGradient(gx, gy, 0, gx, gy, 12);
		gl.addColorStop(0, rgba(acc, 0.55));
		gl.addColorStop(1, rgba(acc, 0));
		ctx.fillStyle = gl;
		ctx.beginPath();
		ctx.arc(gx, gy, 12, 0, TAU);
		ctx.fill();
		ctx.fillStyle = rgba(acc, 1);
		ctx.beginPath();
		ctx.arc(gx, gy, 4, 0, TAU);
		ctx.fill();

		ctx.font = "600 12px 'Hanken Grotesk', sans-serif";
		ctx.fillStyle = "rgba(255,107,107,0.9)";
		ctx.textAlign = "left";
		ctx.fillText(L.traditional, x0 + 2, topY - 9);
		ctx.fillStyle = rgba(acc, 0.95);
		ctx.fillText(L.graphless, x0 + 2, baseY - 8);

		const sleeping = tv < 0.22;
		ctx.font = "11px 'JetBrains Mono', monospace";
		ctx.fillStyle = sleeping ? rgba(acc, 0.9) : "rgba(200,210,214,0.85)";
		ctx.textAlign = gx > W - 90 ? "right" : "left";
		ctx.fillText(
			sleeping ? L.sleeping : L.active,
			gx + (ctx.textAlign === "right" ? -8 : 8),
			gy - 10,
		);

		ctx.font = "10px 'JetBrains Mono', monospace";
		ctx.fillStyle = "rgba(150,160,164,0.55)";
		ctx.textAlign = "center";
		ctx.fillText("00h", X(0.04), baseY + 18);
		ctx.fillText("12h", X(0.5), baseY + 18);
		ctx.fillText("24h", X(0.96), baseY + 18);
	};
}

function scaleZero(): DrawFn {
	return (ctx, W, H, t) => {
		ctx.clearRect(0, 0, W, H);
		const topPad = 12;
		const baseY = H - 14;
		const amp = baseY - topPad;
		const primary = getPrimaryRgb();
		ctx.strokeStyle = "rgba(255,255,255,0.10)";
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(0, baseY);
		ctx.lineTo(W, baseY);
		ctx.stroke();
		const off = (t * 0.00007) % 1;
		const burst = (x: number): number => {
			let v = 0.07 + 0.045 * Math.sin(x * 11 + t * 0.0022);
			for (let k = -1; k < 3; k++) {
				const c = k * 0.6 + (0.3 - off);
				const d = x - c;
				v = Math.max(v, Math.exp(-(d * d) / (2 * 0.018)));
			}
			return v;
		};
		const N = 130;
		ctx.beginPath();
		for (let i = 0; i <= N; i++) {
			const u = i / N;
			const y = baseY - burst(u) * amp;
			if (i) ctx.lineTo(u * W, y);
			else ctx.moveTo(u * W, y);
		}
		ctx.strokeStyle = rgba(primary, 0.92);
		ctx.lineWidth = 2.4;
		ctx.stroke();
		ctx.lineTo(W, baseY);
		ctx.lineTo(0, baseY);
		ctx.closePath();
		const g = ctx.createLinearGradient(0, topPad, 0, baseY);
		g.addColorStop(0, rgba(primary, 0.3));
		g.addColorStop(1, rgba(primary, 0.02));
		ctx.fillStyle = g;
		ctx.fill();
		const cx = 0.5;
		const lvl = burst(cx);
		const cy = baseY - lvl * amp;
		const active = lvl > 0.16;
		const gl = ctx.createRadialGradient(cx * W, cy, 0, cx * W, cy, 13);
		gl.addColorStop(0, rgba(primary, 0.6));
		gl.addColorStop(1, rgba(primary, 0));
		ctx.fillStyle = gl;
		ctx.beginPath();
		ctx.arc(cx * W, cy, 13, 0, TAU);
		ctx.fill();
		ctx.fillStyle = active ? rgba(primary, 1) : "rgba(150,160,164,0.9)";
		ctx.beginPath();
		ctx.arc(cx * W, cy, 3.6, 0, TAU);
		ctx.fill();
	};
}

export function timeTravel(labels?: string[]): DrawFn {
	const L = labels ?? DEFAULT_TIME_TRAVEL_LABELS;
	return (ctx, W, H, t) => {
		ctx.clearRect(0, 0, W, H);
		const lineY = H * 0.66;
		const x0 = 14;
		const x1 = W - 14;
		const cyc = (t % 5200) / 5200;
		const head = cyc < 0.78 ? 1 - easeIO(cyc / 0.78) : (cyc - 0.78) / 0.22;
		const hx = lerp(x0, x1, head);
		const primary = getPrimaryRgb();
		const acc2 = getAccent2Rgb();

		ctx.strokeStyle = "rgba(255,255,255,0.14)";
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.moveTo(x0, lineY);
		ctx.lineTo(x1, lineY);
		ctx.stroke();

		ctx.strokeStyle = rgba(primary, 0.9);
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.moveTo(hx, lineY);
		ctx.lineTo(x1, lineY);
		ctx.stroke();

		const labels = L;
		for (let i = 0; i < 4; i++) {
			const u = i / 3;
			const x = lerp(x0, x1, u);
			const passed = x >= hx - 1;
			ctx.fillStyle = passed ? rgba(primary, 1) : "rgba(120,132,140,0.8)";
			ctx.beginPath();
			ctx.arc(x, lineY, i === 3 ? 4 : 3, 0, TAU);
			ctx.fill();
			ctx.font = "10px 'JetBrains Mono', monospace";
			ctx.textAlign = "center";
			ctx.fillStyle = passed ? "rgba(200,210,214,0.8)" : "rgba(120,132,140,0.5)";
			ctx.fillText(labels[i] ?? "", x, lineY + 18);
		}

		const gx = hx;
		const gy = H * 0.3;
		const nodes = Math.round(2 + head * 4);
		const gl = ctx.createRadialGradient(gx, gy, 0, gx, gy, 16);
		gl.addColorStop(0, rgba(primary, 0.28));
		gl.addColorStop(1, rgba(primary, 0));
		ctx.fillStyle = gl;
		ctx.beginPath();
		ctx.arc(gx, gy, 16, 0, TAU);
		ctx.fill();
		for (let i = 0; i < nodes; i++) {
			const a = (i / nodes) * TAU + head * 2;
			const r = 9;
			const nx = gx + Math.cos(a) * r;
			const ny = gy + Math.sin(a) * r * 0.7;
			ctx.strokeStyle = rgba(acc2, 0.5);
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(gx, gy);
			ctx.lineTo(nx, ny);
			ctx.stroke();
			ctx.fillStyle = rgba(primary, 0.9);
			ctx.beginPath();
			ctx.arc(nx, ny, 2, 0, TAU);
			ctx.fill();
		}

		ctx.strokeStyle = rgba(primary, 0.9);
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.moveTo(hx, gy + 18);
		ctx.lineTo(hx, lineY);
		ctx.stroke();
		ctx.fillStyle = rgba(primary, 1);
		ctx.beginPath();
		ctx.arc(hx, lineY, 4.5, 0, TAU);
		ctx.fill();
	};
}

function vectorized(): DrawFn {
	return (ctx, W, H, t) => {
		ctx.clearRect(0, 0, W, H);
		const cols = 16;
		const rows = 5;
		const gap = 3;
		const gw = W - 16;
		const gh = H - 38;
		const cellW = (gw - (cols - 1) * gap) / cols;
		const cellH = (gh - (rows - 1) * gap) / rows;
		const ox = 8;
		const oy = 10;
		const cyc = (t % 3000) / 3000;
		const lit = Math.floor(cyc * (cols + 4));
		const primary = getPrimaryRgb();
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < cols; c++) {
				const x = ox + c * (cellW + gap);
				const y = oy + r * (cellH + gap);
				const dist = lit - c;
				let a = 0.06;
				if (dist >= 0 && dist < 4) a = 0.85 - dist * 0.18;
				rr(ctx, x, y, cellW, cellH, 2);
				ctx.fillStyle = a > 0.1 ? rgba(primary, a) : "rgba(255,255,255,0.05)";
				ctx.fill();
			}
		}
	};
}

export function compat(labels?: CompatLabels): DrawFn {
	const L = labels ?? DEFAULT_COMPAT_LABELS;
	return (ctx, W, H, t) => {
		ctx.clearRect(0, 0, W, H);
		const midY = H * 0.5;
		const ax = W * 0.16;
		const bx = W * 0.84;
		const drawNode = (x: number, label: string, col: string): void => {
			rr(ctx, x - 30, midY - 16, 60, 32, 8);
			ctx.fillStyle = "rgba(255,255,255,0.04)";
			ctx.fill();
			ctx.strokeStyle = rgba(col, 0.5);
			ctx.lineWidth = 1;
			ctx.stroke();
			ctx.font = "10px 'JetBrains Mono', monospace";
			ctx.textAlign = "center";
			ctx.fillStyle = rgba(col, 0.95);
			ctx.fillText(label, x, midY + 3.5);
		};
		ctx.strokeStyle = "rgba(255,255,255,0.12)";
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.moveTo(ax + 30, midY);
		ctx.lineTo(bx - 30, midY);
		ctx.stroke();
		const span = bx - 30 - (ax + 30);
		const primary = getPrimaryRgb();
		const acc2 = getAccent2Rgb();
		for (let i = 0; i < 3; i++) {
			const ph = (t / 1600 + i / 3) % 1;
			const x = ax + 30 + span * ph;
			ctx.fillStyle = rgba(primary, Math.sin(ph * Math.PI));
			ctx.beginPath();
			ctx.arc(x, midY, 2.6, 0, TAU);
			ctx.fill();
			const ph2 = (t / 1600 + i / 3 + 0.16) % 1;
			const x2 = bx - 30 - span * ph2;
			ctx.fillStyle = rgba(acc2, Math.sin(ph2 * Math.PI) * 0.8);
			ctx.beginPath();
			ctx.arc(x2, midY + 6, 2.2, 0, TAU);
			ctx.fill();
		}
		drawNode(ax, L.yourApp, DIM_RGB);
		drawNode(bx, "Graphless", primary);
	};
}

function similarity(): DrawFn {
	const pts: Array<{ x: number; y: number }> = [];
	let seeded = false;

	return (ctx, W, H, t) => {
		if (!seeded) {
			pts.length = 0;
			for (let i = 0; i < 26; i++) {
				pts.push({
					x: 0.1 + Math.random() * 0.8,
					y: 0.15 + Math.random() * 0.7,
				});
			}
			seeded = true;
		}
		ctx.clearRect(0, 0, W, H);
		const cyc = (t % 4000) / 4000;
		const qx = 0.3 + 0.4 * (0.5 + 0.5 * Math.sin(t / 1900));
		const qy = 0.45 + 0.18 * Math.cos(t / 1500);
		const QX = qx * W;
		const QY = qy * H;
		const primary = getPrimaryRgb();
		const acc2 = getAccent2Rgb();
		const withD = pts
			.map((p) => ({ p, d: Math.hypot(p.x - qx, p.y - qy) }))
			.sort((a, b) => a.d - b.d);
		const k = 5;
		withD.forEach(({ p }, idx) => {
			const near = idx < k;
			const px = p.x * W;
			const py = p.y * H;
			if (near) {
				const reach = easeIO(Math.min(1, cyc / 0.6));
				ctx.strokeStyle = rgba(primary, 0.5 * reach);
				ctx.lineWidth = 1;
				ctx.beginPath();
				ctx.moveTo(QX, QY);
				ctx.lineTo(lerp(QX, px, reach), lerp(QY, py, reach));
				ctx.stroke();
			}
			ctx.fillStyle = near ? rgba(primary, 0.95) : "rgba(150,160,164,0.45)";
			ctx.beginPath();
			ctx.arc(px, py, near ? 3 : 2, 0, TAU);
			ctx.fill();
		});
		const gl = ctx.createRadialGradient(QX, QY, 0, QX, QY, 14);
		gl.addColorStop(0, rgba(acc2, 0.5));
		gl.addColorStop(1, rgba(acc2, 0));
		ctx.fillStyle = gl;
		ctx.beginPath();
		ctx.arc(QX, QY, 14, 0, TAU);
		ctx.fill();
		ctx.fillStyle = rgba(acc2, 1);
		ctx.beginPath();
		ctx.arc(QX, QY, 4, 0, TAU);
		ctx.fill();
	};
}

export const CONCEPT_ANIMATIONS = {
	"anim-cost": costCurve,
	"anim-scalezero": scaleZero,
	"anim-timetravel": timeTravel,
	"anim-vectorized": vectorized,
	"anim-compat": compat,
	"anim-similarity": similarity,
} as const satisfies Record<string, () => DrawFn>;

export type ConceptAnimId = keyof typeof CONCEPT_ANIMATIONS;
